import { computed } from "mobx";
import { useMemo } from "react";
import { useFilterSettingsStore, useSaleCompStore } from "..";
import ISaleComp, { SaleCompPropertyType } from "../interfaces/ISaleComp";
import { getMinMax } from "../utils/utils";

export default function (saleComps: ISaleComp[]): ISaleComp[] {
    const { filters: { saleCompSettings: saleCompFilters } } = useFilterSettingsStore();

    const numberFilteredSaleComps = useMemo(() => {
        return saleComps.filter(sc =>
            (!saleCompFilters.minUsableLandSize || sc.landSizeUsable && sc.landSizeUsable >= saleCompFilters.minUsableLandSize) &&
            (!saleCompFilters.maxUsableLandSize || sc.landSizeUsable && sc.landSizeUsable <= saleCompFilters.maxUsableLandSize) &&
            (!saleCompFilters.minParcelSize || sc.parcelSizeAcres && sc.parcelSizeAcres >= saleCompFilters.minParcelSize) &&
            (!saleCompFilters.maxParcelSize || sc.parcelSizeAcres && sc.parcelSizeAcres <= saleCompFilters.maxParcelSize) &&
            (!saleCompFilters.minBuildingSize || sc.buildingSize && sc.buildingSize >= saleCompFilters.minBuildingSize) &&
            (!saleCompFilters.maxBuildingSize || sc.buildingSize && sc.buildingSize <= saleCompFilters.maxBuildingSize) &&
            (!saleCompFilters.minSalePrice || sc.salePrice && sc.salePrice >= saleCompFilters.minSalePrice) &&
            (!saleCompFilters.maxSalePrice || sc.salePrice && sc.salePrice <= saleCompFilters.maxSalePrice) &&
            (!saleCompFilters.minSaleDate || sc.saleDate && sc.saleDate >= saleCompFilters.minSaleDate) &&
            (!saleCompFilters.maxSaleDate || sc.saleDate && sc.saleDate <= saleCompFilters.maxSaleDate) &&
            (!saleCompFilters.minDateModified || sc.modifiedDate && sc.modifiedDate >= saleCompFilters.minDateModified) &&
            (!saleCompFilters.maxDateModified || sc.modifiedDate && sc.modifiedDate <= saleCompFilters.maxDateModified)
        );
    }, [
        saleComps,
        saleCompFilters.minUsableLandSize, saleCompFilters.maxUsableLandSize,
        saleCompFilters.minParcelSize, saleCompFilters.maxParcelSize,
        saleCompFilters.minBuildingSize, saleCompFilters.maxBuildingSize,
        saleCompFilters.minSalePrice, saleCompFilters.maxSalePrice,
        saleCompFilters.minSaleDate, saleCompFilters.maxSaleDate,
        saleCompFilters.minDateModified, saleCompFilters.maxDateModified,
    ]);

    return useMemo(() => {
        if (!saleCompFilters.propertyTypes.length)
            return numberFilteredSaleComps;

        return numberFilteredSaleComps.filter(l => saleCompFilters.propertyTypes.includes(l.propertyType as SaleCompPropertyType));
    }, [numberFilteredSaleComps, saleCompFilters.propertyTypes]);
}

export const useMinMaxUsableLandSize = () => {
    const saleCompStore = useSaleCompStore();
    return useMemo(() => computed(() => getMinMax(saleCompStore.saleComps, saleComp => saleComp.landSizeUsable)), []).get();
}

export const useMinMaxParcelSize = () => {
    const saleCompStore = useSaleCompStore();
    return useMemo(() => computed(() => getMinMax(saleCompStore.saleComps, saleComp => saleComp.parcelSizeAcres)), []).get();
}

export const useMinMaxBuildingSize = () => {
    const saleCompStore = useSaleCompStore();
    return useMemo(() => computed(() => getMinMax(saleCompStore.saleComps, saleComp => saleComp.buildingSize)), []).get();
}

export const useMinMaxSalePrice = () => {
    const saleCompStore = useSaleCompStore();
    return useMemo(() => computed(() => getMinMax(saleCompStore.saleComps, saleComp => saleComp.salePrice)), []).get();
}